import {
	Autocomplete,
	Box,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography
} from "@mui/material";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, RegisterOptions, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";

import {
	useGetOwnerTypesQuery,
	useGetSiteConfigQuery,
	useGetSocietyConfigQuery
} from "../../../../redux/reducers/settings.reducer";
import { IAuthorizedMemberDetails, IAuthorizedVehicleDetails } from "..";
import {
	BUILDING_TYPES_E,
	DEFAULT_VALIDATIONS,
	NUMBER_PLATE_REGEX,
	VEHICLE_TYPES_LIST
} from "../../../../utils/constants";
import {
	useAddAuthorizedMemberMutation,
	useAddVehicleMutation,
	useDeleteMemberMutation,
	useDeleteVehicleMutation,
	useEditAuthorizedMemberMutation
} from "../../../../redux/reducers/dataManagement.reducers";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers-pro";
import {
	IAddAuthorizedMemberRequestData,
	IAddVehicleRequestData,
	IEditAuthorizedMemberRequestData
} from "../../../../interfaces";
import moment, { Moment } from "moment";
import ConfirmationDialog from "../../../../common/ConfirmationDialog";
import NumberInput from "../../../../common/NumberInput";
import { RESTRICTED_ACCESS_E, convertToTitleCase, getRestrictedAccessList } from "../../../../utils/commonUtils";
import { useAppSelector } from "../../../../redux";

interface IAuthorizedMemberDetailsDialogProps {
	open: boolean;
	memberDetails: IAuthorizedMemberDetails | null;
	onClose: () => void;
}

type IAddedVehicleDetails = Omit<IAuthorizedVehicleDetails, "id">;

interface IAddedVehicleErrors {
	[index: number]: string;
}

interface IFlatDetails {
	key: string;
	building: string;
	flatNumber: string;
}

interface IFormFields {
	name: string;
	contact_number: string;
	owner_type: number;
	flat_no: string;
	validity_start_date: Moment | null;
	validity_end_date: Moment | null;
}

const AuthorizedMemberDetailsDialog: FC<IAuthorizedMemberDetailsDialogProps> = (props) => {
	const { open, memberDetails, onClose } = props;

	const authState = useAppSelector((state) => state.auth);

	const [existingVehiclesList, setExistingVehiclesList] = useState<IAuthorizedVehicleDetails[]>([]);
	const [addedVehiclesList, setAddedVehiclesList] = useState<IAddedVehicleDetails[]>([]);

	const [deletingVehicleIds, setDeletingVehicleIds] = useState<number[]>([]);
	const [deletingAddedVehicleIds, setDeletingAddedVehicleIds] = useState<number[]>([]);

	const [addedVehiclesError, setAddedVehiclesError] = useState<IAddedVehicleErrors>({});
	const [existingVehiclesError, setExistingVehiclesError] = useState<IAddedVehicleErrors>({});

	const [showValidityInputs, setShowValidityInputs] = useState<boolean>(false);

	const [showSaveButtonLoader, setShowSaveButtonLoader] = useState<boolean>(false);
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);

	const [customFlatSelectionEnabled, setCustomFlatSelectionEnabled] = useState<boolean>(false);

	const { data: getOwnerTypesResponse } = useGetOwnerTypesQuery();
	const ownerTypesList = getOwnerTypesResponse?.results ?? [];

	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery();
	const societyConfig = useMemo(() => getSocietyConfigResponse?.results ?? [], [getSocietyConfigResponse]);

	// const memberOwnerTypeId = ownerTypesList?.find((ownerTypeItem) => ownerTypeItem.type === "Member")?.id ?? -1;
	// const tenantOwnerTypeId = ownerTypesList?.find((ownerTypeItem) => ownerTypeItem.type === "Tenant")?.id ?? -1;

	const [addVehicle] = useAddVehicleMutation();
	const [deleteVehicle] = useDeleteVehicleMutation();

	const [
		editAuthorizedMember,
		{ isSuccess: editMemberSuccess, error: editMemberError, reset: resetEditAuthorizedMemberState }
	] = useEditAuthorizedMemberMutation();

	const [
		addAuthorizedMember,
		{ data: addMemberResponse, error: addMemberError, reset: resetAddAuthorizedMemberState }
	] = useAddAuthorizedMemberMutation();

	const [
		deleteMember,
		{
			isLoading: deleteMemberLoading,
			isSuccess: deleteMemberSuccess,
			error: deleteMemberError,
			reset: resetDeleteMemberState
		}
	] = useDeleteMemberMutation();

	const { data: getSiteConfigResponse } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	const FlatsList = useMemo<IFlatDetails[]>(() => {
		const flatsList: IFlatDetails[] = [];

		if (societyConfig && societyConfig.length > 0) {
			for (const buildingDetails of societyConfig[0].building_config) {
				for (const buildingEntry of buildingDetails.entries) {
					let key = buildingEntry;

					if (buildingDetails.building_type === BUILDING_TYPES_E.BUILDING) {
						key = `${buildingDetails.name}${buildingDetails.delimiter || "-"}${buildingEntry}`;
					}

					flatsList.push({
						key: key,
						building: buildingDetails.name,
						flatNumber: buildingEntry
					});
				}
			}
		}

		return flatsList;
	}, [societyConfig]);

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(getSiteConfigResponse?.cloud_sync_enabled ?? true),
		[getSiteConfigResponse]
	);

	const {
		register,
		control,
		reset,
		handleSubmit,
		watch,
		formState: { errors }
	} = useForm<IFormFields>();

	const selectedOwnerType = watch("owner_type");

	function handleOpenDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(true);
	}

	function handleCloseDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(false);
	}

	function handleAddNewVehicle() {
		setAddedVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList.push({
				vehicle_number: "",
				vehicle_type: 0
			});

			return updatedVehiclesList;
		});
	}

	function handleChangeExistingVehicleType(index: number, updatedValue: number) {
		setExistingVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList[index] = {
				...updatedVehiclesList[index],
				vehicle_type: updatedValue
			};

			return updatedVehiclesList;
		});
	}

	function handleChangeExistingVehicleNumber(index: number, updatedValue: string) {
		setExistingVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList[index] = {
				...updatedVehiclesList[index],
				vehicle_number: updatedValue
			};

			return updatedVehiclesList;
		});
	}

	function handleChangeAddedVehicleType(index: number, updatedValue: number) {
		setAddedVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList[index] = {
				...updatedVehiclesList[index],
				vehicle_type: updatedValue
			};

			return updatedVehiclesList;
		});
	}

	function handleChangeAddedVehicleNumber(index: number, updatedValue: string) {
		setAddedVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList[index] = {
				...updatedVehiclesList[index],
				vehicle_number: updatedValue
			};

			return updatedVehiclesList;
		});
	}

	function handleDeleteExistingVehicle(vehicleId: number) {
		setDeletingVehicleIds((currentVehicleIds) => {
			const updatedVehicleIds = [...currentVehicleIds];
			updatedVehicleIds.push(vehicleId);
			return updatedVehicleIds;
		});
	}

	function handleRevertDeletedExistingVehicle(vehicleId: number) {
		setDeletingVehicleIds((currentVehicleIds) => {
			const updatedVehicleIds = currentVehicleIds.filter((item) => item !== vehicleId);
			return updatedVehicleIds;
		});
	}

	function handleDeleteAddedVehicle(index: number) {
		setDeletingAddedVehicleIds((currentVehicleIds) => {
			const updatedVehicleIds = [...currentVehicleIds];
			updatedVehicleIds.push(index);
			return updatedVehicleIds;
		});
	}

	function handleRevertDeletedAddedVehicle(index: number) {
		setDeletingAddedVehicleIds((currentVehicleIds) => {
			const updatedVehicleIds = [...currentVehicleIds];
			updatedVehicleIds.splice(index, 1);
			return updatedVehicleIds;
		});
	}

	const createAddedVehicles = useCallback(
		async (currentMemberDetails: IAuthorizedMemberDetails) => {
			const updatedAddedVehiclesErrors: IAddedVehicleErrors = {};
			const updatedExistingVehiclesErrors: IAddedVehicleErrors = {};

			const updatedExistingVehiclesList = [...existingVehiclesList];
			const updatedAddedVehiclesList = [...addedVehiclesList];

			for (const vehicleId of deletingVehicleIds) {
				const response = await deleteVehicle(vehicleId);

				if ("error" in response && "data" in response.error) {
					updatedExistingVehiclesErrors[vehicleId] = response.error.data
						? String(response.error.data)
						: "Invalid vehicle number";
				} else {
					const index = updatedExistingVehiclesList.findIndex((item) => item.id === vehicleId);
					if (index >= 0) updatedExistingVehiclesList.splice(index, 1);
				}
			}

			for (const [index, vehicleItem] of Object.entries(addedVehiclesList)) {
				if (deletingAddedVehicleIds.includes(Number(index))) {
					updatedAddedVehiclesList.splice(Number(index), 1);
					continue;
				}

				if (!NUMBER_PLATE_REGEX.test(vehicleItem.vehicle_number.toUpperCase())) {
					updatedAddedVehiclesErrors[Number(index)] = "Invalid vehicle number";
				}

				const formData: IAddVehicleRequestData = {
					owner: currentMemberDetails.id,
					number_plate: vehicleItem.vehicle_number.toUpperCase(),
					vehicle_type: vehicleItem.vehicle_type,
					comment: "",
					auth_type: "whitelist"
				};

				// if (
				// 	currentMemberDetails.owner_type_id === tenantOwnerTypeId &&
				// 	currentMemberDetails.validity_start_date &&
				// 	currentMemberDetails.validity_end_date
				// ) {
				// 	formData.validity_start_date = moment(currentMemberDetails.validity_start_date).format("YYYY-MM-DD");
				// 	formData.validity_end_date = moment(currentMemberDetails.validity_end_date).format("YYYY-MM-DD");
				// }

				if (!updatedAddedVehiclesErrors[Number(index)]) {
					const response = await addVehicle(formData);

					if ("error" in response && "data" in response.error) {
						updatedAddedVehiclesErrors[Number(index)] = response.error.data
							? String(response.error.data)
							: "Invalid vehicle number";
					} else if ("data" in response) {
						updatedExistingVehiclesList.push({
							id: response.data.id,
							vehicle_number: response.data.number_plate,
							vehicle_type: response.data.vehicle_type
						});

						updatedAddedVehiclesList.splice(Number(index), 1);
					}
				}
			}

			setAddedVehiclesError(updatedAddedVehiclesErrors);
			setExistingVehiclesError(updatedExistingVehiclesErrors);
			setExistingVehiclesList(updatedExistingVehiclesList);
			setAddedVehiclesList(updatedAddedVehiclesList);

			setShowSaveButtonLoader(false);

			if (
				Object.keys(updatedAddedVehiclesErrors).length <= 0 &&
				Object.keys(updatedExistingVehiclesErrors).length <= 0
			) {
				onClose();
				resetEditAuthorizedMemberState();
				resetAddAuthorizedMemberState();
			}
		},
		[
			addVehicle,
			addedVehiclesList,
			deleteVehicle,
			deletingAddedVehicleIds,
			deletingVehicleIds,
			existingVehiclesList,
			onClose,
			resetAddAuthorizedMemberState,
			resetEditAuthorizedMemberState
		]
	);

	function handleSubmitForm(data: IFormFields) {
		setShowSaveButtonLoader(true);

		const selectedOwnerTypeDetails = ownerTypesList.find(
			(ownerTypeItem) => ownerTypeItem.id === Number(data.owner_type)
		);

		if (memberDetails) {
			const formData: IEditAuthorizedMemberRequestData = {
				id: memberDetails.id,
				data: {
					name: data.name,
					phone_number: data.contact_number,
					owner_type: Number(data.owner_type),
					owner: selectedOwnerTypeDetails?.type ?? "",
					// society: societyConfig && societyConfig.length > 0 ? societyConfig[0].id : 1,
					// flat_number:
					// 	Number(data.owner_type) === memberOwnerTypeId || Number(data.owner_type) === tenantOwnerTypeId
					// 		? data.flat_no
					// 		: ""
					flat_number: data.flat_no,
					is_custom_flat_number: customFlatSelectionEnabled
				}
			};

			if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
				formData.data.validity_start_date = moment(data.validity_start_date).format("YYYY-MM-DD");
				formData.data.validity_end_date = moment(data.validity_end_date).format("YYYY-MM-DD");
			}

			editAuthorizedMember(formData);
		} else {
			if (addMemberResponse) {
				const formData: IEditAuthorizedMemberRequestData = {
					id: addMemberResponse.id,
					data: {
						name: addMemberResponse.name,
						phone_number: addMemberResponse.phone_number,
						owner_type: addMemberResponse.owner_type,
						owner: selectedOwnerTypeDetails?.type ?? "",
						// society: societyConfig && societyConfig.length > 0 ? societyConfig[0].id : 1,
						flat_number: addMemberResponse.flat_number,
						is_custom_flat_number: customFlatSelectionEnabled
					}
				};

				if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
					formData.data.validity_start_date = moment(data.validity_start_date).format("YYYY-MM-DD");
					formData.data.validity_end_date = moment(data.validity_end_date).format("YYYY-MM-DD");
				}

				editAuthorizedMember(formData);
			} else {
				const formData: IAddAuthorizedMemberRequestData = {
					name: data.name,
					phone_number: data.contact_number,
					flat_number: data.flat_no,
					// flat_number:
					// 	Number(data.owner_type) === memberOwnerTypeId || Number(data.owner_type) === tenantOwnerTypeId
					// 		? data.flat_no
					// 		: "",
					owner_type: Number(data.owner_type),
					// society: societyConfig && societyConfig.length > 0 ? societyConfig[0].id : 1,
					is_custom_flat_number: customFlatSelectionEnabled
				};

				if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
					formData.validity_start_date = moment(data.validity_start_date).format("YYYY-MM-DD");
					formData.validity_end_date = moment(data.validity_end_date).format("YYYY-MM-DD");
				}

				addAuthorizedMember(formData);
			}
		}
	}

	function handleDeleteMember() {
		if (memberDetails) {
			deleteMember(memberDetails.id);
		}
	}

	function handleChangeCustomFlatSelectionEnabled(isChecked: boolean) {
		setCustomFlatSelectionEnabled(isChecked);
	}

	function getValidationError(
		errorKey: keyof IFormFields,
		type: "required" | "pattern" | "max_length",
		limit?: number
	): string {
		const requiredErrorTemplate = "{key} is required";
		const patternErrorTemplate = "Enter a valid {key}";
		const maxLengthErrorTemplate = `{key} should not be longer than ${limit ?? 0} characters`;

		let key = "";

		if (errorKey === "name") key = "name";
		else if (errorKey === "contact_number") key = "contact number";
		else if (errorKey === "flat_no") key = "unit number";

		if (type === "required") return requiredErrorTemplate.replace("{key}", convertToTitleCase(key));
		if (type === "pattern") return patternErrorTemplate.replace("{key}", key);
		if (type === "max_length") return maxLengthErrorTemplate.replace("{key}", convertToTitleCase(key));

		return "";
	}

	function getValidations<T extends keyof IFormFields>(key: T): RegisterOptions<IFormFields, T> {
		if (!DEFAULT_VALIDATIONS) return {};

		let validationOptions = null;

		if (key === "name") validationOptions = DEFAULT_VALIDATIONS.name;
		else if (key === "contact_number") validationOptions = DEFAULT_VALIDATIONS.phone_number;
		else if (key === "flat_no") validationOptions = DEFAULT_VALIDATIONS.flat_number;

		if (!validationOptions) return {};

		const validations: RegisterOptions = {};

		if (!validationOptions.allow_null) {
			validations.required = getValidationError(key, "required");
		}

		if (typeof validationOptions.char_size === "number") {
			validations.maxLength = {
				value: validationOptions.char_size,
				message: getValidationError(key, "max_length", validationOptions.char_size)
			};
		}

		if (validationOptions.regex) {
			validations.pattern = {
				value: validationOptions.regex,
				message: getValidationError(key, "pattern")
			};
		}

		return validations;
	}

	useEffect(() => {
		if (open) {
			if (memberDetails) {
				reset({
					name: memberDetails.name,
					flat_no: memberDetails.flat_no,
					contact_number: memberDetails.contact_number,
					owner_type: memberDetails.owner_type_id,
					validity_start_date: memberDetails.validity_start_date ? moment(memberDetails.validity_start_date) : null,
					validity_end_date: memberDetails.validity_end_date ? moment(memberDetails.validity_end_date) : null
				});

				setExistingVehiclesList(memberDetails.vehicles);
				setCustomFlatSelectionEnabled(memberDetails.is_custom_flat);
			} else {
				reset({
					name: "",
					flat_no: "",
					contact_number: "",
					owner_type: 0,
					validity_start_date: null,
					validity_end_date: null
				});

				setExistingVehiclesList([]);
				setCustomFlatSelectionEnabled(false);
			}

			setAddedVehiclesList([]);
			setDeletingVehicleIds([]);
			setDeletingAddedVehicleIds([]);
			setAddedVehiclesError({});
			setExistingVehiclesError({});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, memberDetails]);

	useEffect(() => {
		if (editMemberSuccess) {
			if (memberDetails) createAddedVehicles(memberDetails);
			else if (addMemberResponse) {
				createAddedVehicles({
					id: addMemberResponse.id,
					sr_no: 0,
					name: addMemberResponse.name,
					flat_no: addMemberResponse.flat_number,
					owner_type:
						ownerTypesList?.find((ownerTypeItem) => ownerTypeItem.id === addMemberResponse.owner_type)?.type ?? "",
					owner_type_id: addMemberResponse.owner_type,
					contact_number: addMemberResponse.phone_number,
					validity_start_date: addMemberResponse.validity_start_date,
					validity_end_date: addMemberResponse.validity_end_date,
					vehicles: [],
					is_custom_flat: addMemberResponse.is_custom_flat_number
				});
			}
		} else if (addMemberResponse) {
			createAddedVehicles({
				id: addMemberResponse.id,
				sr_no: 0,
				name: addMemberResponse.name,
				flat_no: addMemberResponse.flat_number,
				owner_type:
					ownerTypesList?.find((ownerTypeItem) => ownerTypeItem.id === addMemberResponse.owner_type)?.type ?? "",
				owner_type_id: addMemberResponse.owner_type,
				contact_number: addMemberResponse.phone_number,
				validity_start_date: addMemberResponse.validity_start_date,
				validity_end_date: addMemberResponse.validity_end_date,
				vehicles: [],
				is_custom_flat: addMemberResponse.is_custom_flat_number
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addMemberResponse, editMemberSuccess]);

	useEffect(() => {
		if (deleteMemberSuccess) {
			resetDeleteMemberState();
			handleCloseDeleteConfirmationDialog();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteMemberSuccess]);

	useEffect(() => {
		if (selectedOwnerType) {
			const selectedOwnerTypeDetails = ownerTypesList.find(
				(ownerTypeItem) => ownerTypeItem.id === Number(selectedOwnerType)
			);

			if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
				setShowValidityInputs(true);
			} else {
				setShowValidityInputs(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOwnerType]);

	useEffect(() => {
		if (FlatsList.length > 0) {
			setCustomFlatSelectionEnabled(false);
		} else {
			setCustomFlatSelectionEnabled(true);
		}
	}, [FlatsList.length]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			sx={{
				"& .MuiDialog-paper": {
					margin: { xs: "1rem", sm: "2rem" },
					width: { xs: "calc(100% - 2rem)", sm: "auto" },
					maxWidth: { xs: "100%", sm: "900px" },
					maxHeight: { xs: "calc(100vh - 2rem)", sm: "90vh" }
				}
			}}
		>
			<DialogTitle
				textAlign="center"
				sx={{
					background: "#30ae3d",
					color: "white",
					position: "relative",
					padding: { xs: "0.75rem 1rem 0.5rem 1rem", sm: "1rem 1.25rem 0.75rem 1.25rem" },
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: 600,
							fontSize: { xs: "1.125rem", sm: "1.25rem" }
						}}
					>
						{memberDetails ? "Edit Authorized Member" : "Add Authorized Member"}
					</Typography>
				</Box>

				<Box sx={{ position: "absolute", top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 12 } }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: { xs: "4px", sm: "6px" },
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent
				sx={{
					padding: { xs: "1rem !important", sm: "1.25rem !important" },
					background: "#ffffff",
					overflowY: "auto"
				}}
			>
				<Grid
					container
					spacing={{ xs: 2, sm: 3 }}
					component="form"
					noValidate
					onSubmit={handleSubmit(handleSubmitForm)}
				>
					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="name-input"
							required
							error={!!errors.name}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="outlined"
							color="primary"
							size="small"
							id="name-input"
							placeholder="Enter the name of person"
							autoComplete="off"
							error={!!errors.name}
							helperText={errors.name && errors.name.message ? errors.name.message.toString() : ""}
							InputProps={{ readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER) }}
							{...register("name", getValidations("name"))}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="contact-number-input"
							error={!!errors.contact_number}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Contact Number
						</InputLabel>

						<NumberInput
							required
							fullWidth
							isContactNumber
							positiveOnly
							disableSigned
							disableDecimal
							variant="outlined"
							color="primary"
							size="small"
							id="contact-number-input"
							placeholder="Enter the contact number"
							autoComplete="off"
							error={!!errors.contact_number}
							helperText={
								errors.contact_number && errors.contact_number.message ? errors.contact_number.message.toString() : ""
							}
							InputProps={{ readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER) }}
							{...register("contact_number", getValidations("contact_number"))}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="owner-type-input"
							error={!!errors.owner_type}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Owner Type
						</InputLabel>

						<Controller
							name="owner_type"
							control={control}
							render={({ field: { value, onChange } }) => (
								<FormControl
									variant="outlined"
									color="primary"
									size="small"
									fullWidth
									required
									error={!!errors.owner_type}
									id="owner-type-input"
								>
									<Select
										required
										fullWidth
										value={value}
										onChange={onChange}
										readOnly={RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER)}
									>
										{ownerTypesList
											? ownerTypesList.map((ownerTypeItem) => (
													<MenuItem value={ownerTypeItem.id} key={ownerTypeItem.id}>
														{ownerTypeItem.type}
													</MenuItem>
											  ))
											: null}
									</Select>

									{errors.owner_type && errors.owner_type.message ? (
										<FormHelperText>{errors.owner_type.message.toString()}</FormHelperText>
									) : null}
								</FormControl>
							)}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="area-of-visit-input"
							error={!!errors.flat_no}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Area of Visit
						</InputLabel>

						{customFlatSelectionEnabled ? (
							<TextField
								required
								fullWidth
								variant="outlined"
								color="primary"
								size="small"
								id="area-of-visit-input"
								placeholder="Enter the area of visit"
								autoComplete="off"
								error={!!errors.flat_no}
								helperText={errors.flat_no && errors.flat_no.message ? errors.flat_no.message.toString() : ""}
								{...register("flat_no", getValidations("flat_no"))}
							/>
						) : (
							<Controller
								name="flat_no"
								control={control}
								defaultValue=""
								rules={getValidations("flat_no")}
								render={({ field: { value, onChange } }) => (
									<Autocomplete
										fullWidth
										size="small"
										id="area-of-visit-input"
										placeholder="Select the area of visit"
										options={FlatsList}
										getOptionLabel={(option) => option.key}
										getOptionKey={(option) => option.key}
										groupBy={(option) => option.building}
										value={FlatsList.find((item) => item.key === value) ?? null}
										onChange={(_, updatedValue) => onChange(updatedValue?.key ?? null)}
										noOptionsText="No Units"
										readOnly={RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER)}
										renderInput={(params) => (
											<TextField
												{...params}
												required
												fullWidth
												variant="outlined"
												color="primary"
												autoComplete="off"
												error={!!errors.flat_no}
												helperText={errors.flat_no && errors.flat_no.message ? errors.flat_no.message.toString() : ""}
											/>
										)}
									/>
								)}
							/>
						)}

						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER) ? (
							<FormControlLabel
								control={
									<Checkbox
										checked={customFlatSelectionEnabled}
										onChange={(_, checked) => handleChangeCustomFlatSelectionEnabled(checked)}
									/>
								}
								label="Custom Area of Visit"
								sx={{
									"& .MuiFormControlLabel-label": {
										fontSize: { xs: "0.8125rem", sm: "0.875rem" }
									}
								}}
							/>
						) : null}
					</Grid>

					{showValidityInputs ? (
						<>
							<Grid item xs={12} md={6}>
								<InputLabel
									required
									htmlFor="validity-start-date-input"
									error={!!errors.validity_start_date}
									sx={{
										fontWeight: 600,
										color: "#3D3D3D",
										marginBottom: "0.5rem",
										fontSize: { xs: "0.8125rem", sm: "0.875rem" }
									}}
								>
									Validity Start Date
								</InputLabel>

								<Controller
									name="validity_start_date"
									control={control}
									rules={{
										required: showValidityInputs ? "Start date is required" : false
									}}
									render={({ field: { value, onChange } }) => (
										<DatePicker
											format="DD/MM/YYYY"
											value={value}
											onChange={onChange}
											readOnly={RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER)}
											slotProps={{
												textField: {
													required: true,
													fullWidth: true,
													color: "primary",
													variant: "outlined",
													size: "small",
													id: "validity-start-date-input",
													placeholder: "Select validity start date",
													error: !!errors.validity_start_date,
													helperText:
														errors.validity_start_date && errors.validity_start_date.message
															? errors.validity_start_date.message.toString()
															: ""
												}
											}}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<InputLabel
									required
									htmlFor="validity-end-date-input"
									error={!!errors.validity_end_date}
									sx={{
										fontWeight: 600,
										color: "#3D3D3D",
										marginBottom: "0.5rem",
										fontSize: { xs: "0.8125rem", sm: "0.875rem" }
									}}
								>
									Validity End Date
								</InputLabel>

								<Controller
									name="validity_end_date"
									control={control}
									rules={{ required: showValidityInputs ? "End date is required" : false }}
									render={({ field: { value, onChange } }) => (
										<DatePicker
											format="DD/MM/YYYY"
											value={value}
											onChange={onChange}
											readOnly={RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER)}
											slotProps={{
												textField: {
													required: true,
													fullWidth: true,
													color: "primary",
													variant: "outlined",
													size: "small",
													id: "validity-end-date-input",
													placeholder: "Select validity end date",
													error: !!errors.validity_end_date,
													helperText:
														errors.validity_end_date && errors.validity_end_date.message
															? errors.validity_end_date.message.toString()
															: ""
												}
											}}
										/>
									)}
								/>
							</Grid>
						</>
					) : null}

					<Grid item xs={12}>
						<Box
							className="vehicles-list-wrapper"
							sx={{
								background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
								borderRadius: "0.5rem",
								padding: { xs: "0.5rem 0.75rem 0.75rem", sm: "0.5rem 1rem 1rem" },
								border: "1px solid rgba(0, 0, 0, 0.08)",
								boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)"
							}}
						>
							<Typography
								variant="h6"
								fontWeight={700}
								sx={{
									color: "#2c3e50",
									display: "flex",
									alignItems: "center",
									gap: 1,
									fontSize: { xs: "1rem", sm: "1.25rem" }
								}}
							>
								<DirectionsCarFilledIcon sx={{ color: "#4caf50", fontSize: { xs: "20px", sm: "24px" } }} />
								Known list of Vehicles
							</Typography>

							{existingVehiclesList.map((vehicleItem, index) => (
								<Box
									sx={{
										border: "1px solid #4caf50",
										borderRadius: "0.5rem",
										display: "flex",
										flexDirection: { xs: "column", sm: "row" },
										alignItems: { xs: "stretch", sm: "center" },
										gap: { xs: 1, sm: 2 },
										padding: { xs: "0.75rem", sm: "1rem" },
										background: "white",
										boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
										transition: "all 0.3s ease",
										"&:hover": {
											boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
										}
									}}
									key={index}
								>
									<Box sx={{ flex: 1 }}>
										<InputLabel
											required
											sx={{
												fontWeight: 600,
												color: "#3D3D3D",
												marginBottom: "0.5rem",
												fontSize: { xs: "0.8125rem", sm: "0.875rem" }
											}}
										>
											Vehicle Type
										</InputLabel>

										<FormControl
											required
											fullWidth
											variant="outlined"
											color="primary"
											size="small"
											disabled={deletingVehicleIds.includes(vehicleItem.id)}
										>
											<Select
												required
												fullWidth
												readOnly
												value={vehicleItem.vehicle_type}
												onChange={(event) => handleChangeExistingVehicleType(index, Number(event.target.value))}
											>
												{VEHICLE_TYPES_LIST.map((ownerTypeItem) => (
													<MenuItem value={ownerTypeItem.id} key={ownerTypeItem.id}>
														{ownerTypeItem.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>

									<Box sx={{ flex: 1 }}>
										<InputLabel
											required
											error={!!existingVehiclesError[vehicleItem.id]}
											sx={{
												fontWeight: 600,
												color: "#3D3D3D",
												marginBottom: "0.5rem",
												fontSize: { xs: "0.8125rem", sm: "0.875rem" }
											}}
										>
											Vehicle Number
										</InputLabel>

										<TextField
											required
											fullWidth
											disabled={deletingVehicleIds.includes(vehicleItem.id)}
											variant="outlined"
											color="primary"
											size="small"
											autoComplete="off"
											placeholder="Enter the vehicle number"
											value={vehicleItem.vehicle_number}
											onChange={(event) => handleChangeExistingVehicleNumber(index, event.target.value)}
											InputProps={{ readOnly: true }}
											error={!!existingVehiclesError[vehicleItem.id]}
											helperText={existingVehiclesError ? existingVehiclesError[vehicleItem.id] : ""}
											classes={{ root: "vehicle-number-input-root" }}
										/>
									</Box>

									{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER) ? (
										<Box
											sx={{
												display: "flex",
												justifyContent: { xs: "flex-end", sm: "center" },
												alignItems: "center"
											}}
										>
											{deletingVehicleIds.includes(vehicleItem.id) ? (
												<IconButton
													onClick={() => handleRevertDeletedExistingVehicle(vehicleItem.id)}
													sx={{
														color: "#4caf50",
														"&:hover": {
															backgroundColor: "rgba(76, 175, 80, 0.1)"
														}
													}}
												>
													<CloseIcon />
												</IconButton>
											) : (
												<IconButton
													onClick={() => handleDeleteExistingVehicle(vehicleItem.id)}
													sx={{
														color: "#f44336",
														"&:hover": {
															backgroundColor: "rgba(244, 67, 54, 0.1)"
														}
													}}
												>
													<DeleteIcon />
												</IconButton>
											)}
										</Box>
									) : null}
								</Box>
							))}

							{addedVehiclesList.map((vehicleItem, index) => (
								<Box
									sx={{
										border: "1px solid #4caf50",
										borderRadius: "0.5rem",
										display: "flex",
										flexDirection: { xs: "column", sm: "row" },
										alignItems: { xs: "stretch", sm: "center" },
										gap: { xs: 1, sm: 2 },
										padding: { xs: "0.75rem", sm: "1rem" },
										background: "white",
										boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
										transition: "all 0.3s ease",
										"&:hover": {
											boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
										}
									}}
									key={index}
								>
									<Box sx={{ flex: 1 }}>
										<InputLabel
											required
											sx={{
												fontWeight: 600,
												color: "#3D3D3D",
												marginBottom: "0.5rem",
												fontSize: { xs: "0.8125rem", sm: "0.875rem" }
											}}
										>
											Vehicle Type
										</InputLabel>

										<FormControl
											required
											fullWidth
											variant="outlined"
											color="primary"
											size="small"
											disabled={deletingAddedVehicleIds.includes(index)}
										>
											<Select
												required
												fullWidth
												value={vehicleItem.vehicle_type}
												onChange={(event) => handleChangeAddedVehicleType(index, Number(event.target.value))}
											>
												{VEHICLE_TYPES_LIST.map((ownerTypeItem) => (
													<MenuItem value={ownerTypeItem.id} key={ownerTypeItem.id}>
														{ownerTypeItem.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>

									<Box sx={{ flex: 1 }}>
										<InputLabel
											required
											error={!!addedVehiclesError[index]}
											sx={{
												fontWeight: 600,
												color: "#3D3D3D",
												marginBottom: "0.5rem",
												fontSize: { xs: "0.8125rem", sm: "0.875rem" }
											}}
										>
											Vehicle Number
										</InputLabel>

										<TextField
											required
											fullWidth
											disabled={deletingAddedVehicleIds.includes(index)}
											variant="outlined"
											color="primary"
											size="small"
											autoComplete="off"
											placeholder="Enter the vehicle number"
											value={vehicleItem.vehicle_number}
											onChange={(event) => handleChangeAddedVehicleNumber(index, event.target.value)}
											error={!!addedVehiclesError[index]}
											helperText={addedVehiclesError ? addedVehiclesError[index] : ""}
											classes={{ root: "vehicle-number-input-root" }}
										/>
									</Box>

									{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER) ? (
										<Box
											sx={{
												display: "flex",
												justifyContent: { xs: "flex-end", sm: "center" },
												alignItems: "center"
											}}
										>
											{deletingAddedVehicleIds.includes(index) ? (
												<IconButton
													onClick={() => handleRevertDeletedAddedVehicle(index)}
													sx={{
														color: "#4caf50",
														"&:hover": {
															backgroundColor: "rgba(76, 175, 80, 0.1)"
														}
													}}
												>
													<CloseIcon />
												</IconButton>
											) : (
												<IconButton
													onClick={() => handleDeleteAddedVehicle(index)}
													sx={{
														color: "#f44336",
														"&:hover": {
															backgroundColor: "rgba(244, 67, 54, 0.1)"
														}
													}}
												>
													<DeleteIcon />
												</IconButton>
											)}
										</Box>
									) : null}
								</Box>
							))}

							{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER) ? (
								<Box
									sx={{
										border: "2px dashed #4caf50",
										borderRadius: "0.5rem",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 1,
										padding: { xs: "1rem", sm: "1.25rem" },
										cursor: "pointer",
										transition: "all 0.3s ease",
										background: "rgba(76, 175, 80, 0.02)",
										"&:hover": {
											background: "rgba(76, 175, 80, 0.08)",
											borderColor: "#66bb6a",
											transform: "translateY(-1px)"
										}
									}}
									onClick={handleAddNewVehicle}
								>
									<AddCircleIcon sx={{ color: "#4caf50", fontSize: { xs: "20px", sm: "24px" } }} />
									<Typography
										fontWeight={600}
										sx={{
											color: "#4caf50",
											fontSize: { xs: "0.875rem", sm: "1rem" }
										}}
									>
										Add new Vehicle
									</Typography>
								</Box>
							) : null}
						</Box>
					</Grid>

					{addMemberError ? (
						<Grid item xs={12}>
							<Typography
								variant="body2"
								color="var(--color-error-main)"
								textAlign="center"
								sx={{
									fontSize: { xs: "0.8125rem", sm: "0.875rem" }
								}}
							>
								{"data" in addMemberError
									? `${String(addMemberError.data).slice(0, 1).toUpperCase()}${String(addMemberError.data).slice(1)}`
									: "Something went wrong"}
							</Typography>
						</Grid>
					) : null}

					{editMemberError ? (
						<Grid item xs={12}>
							<Typography
								variant="body2"
								color="var(--color-error-main)"
								textAlign="center"
								sx={{
									fontSize: { xs: "0.8125rem", sm: "0.875rem" }
								}}
							>
								{"data" in editMemberError
									? `${String(editMemberError.data).slice(0, 1).toUpperCase()}${String(editMemberError.data).slice(1)}`
									: "Something went wrong"}
							</Typography>
						</Grid>
					) : null}

					{deleteMemberError ? (
						<Grid item xs={12}>
							<Typography
								variant="body2"
								color="var(--color-error-main)"
								textAlign="center"
								sx={{
									fontSize: { xs: "0.8125rem", sm: "0.875rem" }
								}}
							>
								{"data" in deleteMemberError
									? `${String(deleteMemberError.data).slice(0, 1).toUpperCase()}${String(deleteMemberError.data).slice(
											1
									  )}`
									: "Something went wrong"}
							</Typography>
						</Grid>
					) : null}

					{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_OWNER) ? (
						<Grid item xs={12}>
							<Box
								sx={{
									display: "flex",
									flexDirection: { xs: "column", sm: "row" },
									alignItems: "center",
									justifyContent: "center",
									gap: { xs: "0.75rem", sm: "1rem" }
								}}
							>
								{memberDetails ? (
									<LoadingButton
										variant="outlined"
										color="error"
										onClick={handleOpenDeleteConfirmationDialog}
										loading={deleteMemberLoading}
										sx={{
											width: { xs: "100%", sm: "auto" },
											fontSize: { xs: "0.875rem", sm: "1rem" },
											padding: { xs: "8px 16px", sm: "8px 16px" }
										}}
									>
										Delete
									</LoadingButton>
								) : null}

								<LoadingButton
									variant="contained"
									color="primary"
									type="submit"
									loading={showSaveButtonLoader}
									sx={{
										width: { xs: "100%", sm: "auto" },
										fontSize: { xs: "0.875rem", sm: "1rem" },
										padding: { xs: "8px 16px", sm: "8px 16px" }
									}}
								>
									Save
								</LoadingButton>
							</Box>
						</Grid>
					) : null}
				</Grid>
			</DialogContent>

			<ConfirmationDialog
				open={showDeleteConfirmationDialog}
				title="Delete Authorized Member"
				heading="Are you sure you want to delete this authorized member?"
				onClose={handleCloseDeleteConfirmationDialog}
				onConfirm={handleDeleteMember}
				confirmButtonLoading={deleteMemberLoading}
				color="error"
			/>
		</Dialog>
	);
};

export default AuthorizedMemberDetailsDialog;
